import {
  Button,
  Backdrop,
  Box,
  Fade,
  Modal,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import {
  gradientBorder,
  variantTextButton,
} from '../../assets/styles/button.style';
import {
  getFileExtension,
  isRESTAPIError,
  setS3UploadFormData,
} from '../../assets/utils/helpers';
import {
  generateFileUploadErrorResponse,
  generateFileUploadToS3BucketErrorResponse,
  generateReportSubmissionErrorResponse,
} from '../../assets/utils/responseFactory';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';
import { CustomError } from '../../interfaces/ErrorType';
import ModalProps from '../../interfaces/ModalProps';
import reportFileService from '../../services/reportFileService';
import reportService from '../../services/reportService';
import AddFileButton from '../reportSubmission/addFileButton';

const style = {
  flexDirection: 'column',
  borderRadius: '.7rem',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px', // Updated box-shadow
  p: 3,
  textAlign: 'center',
};

const AdditionalInfoModalComponent = ({ open, handleClose }: ModalProps) => {
  const navigate = useNavigate();
  const { createToastNotification } = useToastNotificationContext();
  const { reportDetailsId } = useParams();
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // const [error, setError] = useState<ErrorType>({
  //   errorType: false,
  //   message: '',
  // });

  const uploadFileStart = async (file: File) => {
    try {
      const { name } = file;
      return await reportFileService.uploadFileStart({
        fileName: name,
        fileType: getFileExtension(name),
        fileSize: file.size,
      });
    } catch (err: unknown) {
      const errorMessage = 'Error happened. We\'re currently fixing it.';
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadErrorResponse(err.response.status);
        setIsLoading(false);
        throw new Error(resp);
      }
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  const uploadFileActual = async (
    url: string,
    requestBody: FormData,
    name: string,
  ) => {
    try {
      return await reportFileService.uploadFileToS3Bucket({
        url,
        data: requestBody,
      });
    } catch (err: unknown) {
      const errorMessage = 'Error happened. We\'re currently fixing it.';
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadToS3BucketErrorResponse(
          err.response.status,
          name,
        );
        setIsLoading(false);
        throw new Error(resp);
      }
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  const uploadFileFinish = async (id: number, name: string) => {
    try {
      return await reportFileService.uploadFileFinish({ fileId: id });
    } catch (err: unknown) {
      const errorMessage = 'Error happened. We\'re currently fixing it.';
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadErrorResponse(err.response.status, name);
        setIsLoading(false);
        throw new Error(resp);
      }
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  const uploadFile = async (file: File) => {
    const body = await uploadFileStart(file);
    const { url, id = null, fields } = body;
    const requestBody = { ...fields, ...{ file } };
    const requestBodyForS3 = setS3UploadFormData(requestBody);
    await uploadFileActual(url, requestBodyForS3, file.name);
    await uploadFileFinish(id, file.name);

    return id;
  };

  const uploadFiles = async () => {
    const uploadFilesPromise = files.map(async (file) => {
      return uploadFile(file);
    });
    const fileidsOfUploadedFiles = (await Promise.all(uploadFilesPromise)).map(
      (obj) => obj,
    );
    // if (error?.errorType === false) {
    //   // setIsLoading(false);
    // }
    return fileidsOfUploadedFiles;
  };

  const submitReport = async (
    reportId: string,
    idsOfUploadedFiles: number[],
    descriptionText: string,
  ) => {
    try {
      return await reportService.submitAdditionalInformation({
        reportId,
        descriptionText,
        fileIds: idsOfUploadedFiles,
      });
    } catch (err: unknown) {
      const errorMessage = 'Error happened. We\'re currently fixing it.';
      if (isRESTAPIError(err)) {
        const resp = generateReportSubmissionErrorResponse(err.response.status);
        setIsLoading(false);
        throw new Error(resp);
      }
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  const formik = useFormik({
    initialValues: {
      details: '',
      submit: null,
    },
    validationSchema: Yup.object({
      details: Yup.string().required('Need details to submit.'),
    }),
    onSubmit: async (values, helpers) => {
      setIsLoading(true);
      try {
        const fileidsOfUploadedFilesOfUploadedFiles = await uploadFiles();
        const reportData = await submitReport(
          `${reportDetailsId}`,
          fileidsOfUploadedFilesOfUploadedFiles,
          values.details,
        );

        const [uploadResult] = await Promise.allSettled([reportData]);

        if (uploadResult.status === 'fulfilled') {
          createToastNotification('Additional information successfully added.', 'success');
          await new Promise(resolve => setTimeout(resolve, 2000));
          formik.setValues({
            details: '', // Set the field to an empty string
            submit: null, // You can reset other fields as needed
          });
          setFiles([]);
          setIsLoading(false);
          handleClose();
          navigate(`/inbox/${reportDetailsId}`);
          window.location.reload();
        } else {
          setIsLoading(false);
          throw new Error(
            `Error submitting the report: ${uploadResult.reason}`,
          );
        }
      } catch (err) {
        helpers.setStatus({ success: false });

        const customError = err as CustomError;
        const errorMessage =
          customError.message ||
          // customError.request?.responseText ||
          'Something went wrong, please try again.';
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
      }
    },
  });

  const handleCloseModal = () => {
    formik.setValues({
      details: '',
      submit: null,
    });
    setFiles([]);
    handleClose();
  };

  return (
    <Modal
      aria-labelledby='transition-modal-title'
      aria-describedby='transition-modal-description'
      open={open}
      //   onClose={handleClose}  // on exit
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <form noValidate onSubmit={formik.handleSubmit}>
            <Typography
              variant='h4'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '17px',
                letterSpacing: '0em',
                textAlign: 'left',
                mt: '1rem',
                mb: '0.5rem',
              }}
            >
              Additional Information
            </Typography>

            <TextField
              multiline
              rows={5}
              id='filled-textarea'
              placeholder='Kindly add additional information'
              variant='filled'
              fullWidth
              autoComplete='off'
              type='text'
              disabled={isLoading}
              error={!!(formik.touched.details && formik.errors.details)}
              helperText={formik.touched.details && formik.errors.details}
              name='details'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.details}
              sx={{
                ...{
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root': {
                    borderRadius: '6px',
                    padding: '20px',
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '17.28px',
                    color: '#555555',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  mb: '1rem',
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                },
                //   ...(!text && {
                //     '& .MuiInputLabel-root': {
                //       color: '#0000004D',
                //     },
                //   }),
                //   ...(text && {
                //     '& .MuiFilledInput-root': {
                //       backgroundColor: '#FFFFFF',
                //       borderRadius: '6px',
                //       padding: '14px 18px',
                //       border: '2px solid #00000080',
                //     },
                //     '& .MuiFormControl-root .MuiTextField-root': {
                //       border: '2px solid #00000080',
                //       borderRadius: '6px',
                //     },
                //     '& .MuiFilledInput-input': {
                //       fontFamily: 'Schibsted Grotesk',
                //       fontSize: '14px',
                //       fontWeight: 400,
                //       lineHeight: '26px',
                //     },
                //   }),
              }}
            />

            {/* Add file */}
            <AddFileButton
              disabled={isLoading}
              selectedFiles={files}
              setSelectedFiles={setFiles}
            />

            {/* Buttons */}
            <Box sx={{ width: '100%' }}>
              {/* Cancel Button */}
              <Button
                variant='text'
                onClick={handleCloseModal}
                disabled={isLoading}
                sx={{
                  ...variantTextButton,
                  width: 'calc(50% - 0.2rem)',
                  margin: '1rem .1rem .1rem 0',
                }}
              >
                {/* {isLoading ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ marginRight: '8px', color: 'black' }} // Change the CircularProgress color
                    />
                    <span style={{ color: 'black' }}>Loading...</span>
                  </>
                ) : (
                  'Cancel'
                )} */}
                Cancel
              </Button>

              {/* Submit Button */}
              <Button
                variant='outlined'
                type='submit'
                disabled={isLoading}
                sx={{
                  ...gradientBorder,
                  color: 'black',
                  width: 'calc(50% - 0.2rem)',
                  margin: '1rem .1rem .1rem 0',
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ marginRight: '8px', color: 'black' }} // Change the CircularProgress color
                    />
                    <span style={{ color: 'black' }}>Loading...</span>
                  </>
                ) : (
                  'Submit'
                )}
              </Button>

              {/* Error helper */}
              {formik.errors.submit && (
                <Typography color='error' variant='body2' marginTop={1}>
                  {formik.errors.submit}
                </Typography>
              )}
            </Box>
          </form>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AdditionalInfoModalComponent;
